/**
 * Platform-neutral upload asset (ELARA-692).
 *
 * The web `File` type was leaking out of `FileUploadState` into `ChatInputState`'s
 * shared attachment state machine and its UI producers, forking the whole
 * compose/send path on React Native (where `expo-image-picker` returns a `uri`,
 * not a `File`). `Asset` is the neutral handle the shared logic depends on
 * instead: it carries the metadata every producer can supply plus exactly one
 * byte source — a `Blob` on web, a local `uri` on native.
 *
 * `assetToUploadData` resolves either source to the bytes that the shared
 * Firebase Storage orchestration (`FileUploadState`/`StorageContainer`)
 * uploads, so the upload path stays identical across platforms. UI never
 * reads `blob`/`uri` directly — it goes through {@link assetPreviewUrl} —
 * keeping the seam intact.
 */

/** Fields every producer can supply regardless of platform. */
interface AssetMetadata {
  /** File/display name, e.g. `boarding-pass.pdf`. */
  readonly name: string;
  /** MIME type, e.g. `image/jpeg`. Empty string when the producer can't determine it. */
  readonly mimeType: string;
  /** Size in bytes. */
  readonly size: number;
}

/** Web asset: backed directly by the `File`/`Blob` the browser produced. */
export interface BlobAsset extends AssetMetadata {
  readonly blob: Blob;
  readonly uri?: never;
}

/** Native asset: backed by a local file `uri` (e.g. from `expo-image-picker`). */
export interface UriAsset extends AssetMetadata {
  readonly uri: string;
  readonly blob?: never;
}

/**
 * A platform-neutral reference to a user-selected file. Exactly one of `blob`
 * (web) or `uri` (native) is the byte source — the union makes the illegal
 * "neither / both" states unrepresentable.
 */
export type Asset = BlobAsset | UriAsset;

/** Wraps a web `File` as a {@link BlobAsset}, the producer boundary on web. */
export function fileToAsset(file: File): BlobAsset {
  return { name: file.name, mimeType: file.type, size: file.size, blob: file };
}

/**
 * Resolves an asset's byte source to a `Blob` for consumers that need a real
 * `Blob` object (e.g. the web `ImageProcessor`'s `FileReader`/`<canvas>`
 * pipeline). Web assets return their `Blob` directly; native assets are
 * fetched from their local `uri` via `fetch(uri).blob()`.
 *
 * Do not use this for the Firebase Storage upload path — use
 * {@link assetToUploadData} instead, which avoids a React Native Blob-polyfill
 * crash on that same `uri` fetch.
 */
export async function assetToBlob(asset: Asset): Promise<Blob> {
  if (asset.uri !== undefined) {
    const response = await fetch(asset.uri);
    return response.blob();
  }
  return asset.blob;
}

/**
 * Resolves an asset's byte source to a real `Blob` for the Firebase Storage
 * upload path. Web assets return their `Blob` directly; native assets are read
 * from their local `uri` into a native `Blob` via {@link uriToBlob}.
 *
 * It must be a real (native) `Blob` — not an `ArrayBuffer` or `Uint8Array`
 * (ELARA-863). Although `uploadBytesResumable` accepts all three, Firebase
 * Storage's small-file `multipartUpload` path builds the request body with
 * `new Blob([preamble, bytes, epilogue])` (see `FbsBlob.getBlob`), and RN's
 * `Blob` polyfill rejects any `ArrayBuffer`/`ArrayBufferView` part with
 * "Creating blobs from 'ArrayBuffer' and 'ArrayBufferView' are not supported".
 * So handing it an `ArrayBuffer` still crashes — just later, inside the upload
 * task. Worse, that throw surfaces in one of Firebase's internal promises that
 * is never routed to the task's error observer, so the upload neither resolves
 * nor rejects: it hangs, and the composer stays stuck "sending". A native
 * `Blob` is stored by `FbsBlob` as-is and passed straight to `new Blob([...])`,
 * which RN accepts.
 *
 * We can't get that `Blob` from `fetch(uri).blob()` either: for freshly-written
 * local files (camera captures, the document picker's cache copy) RN's
 * networking layer can't always resolve a native `Blob` for the response, and
 * the `whatwg-fetch` polyfill then falls back to `new Blob([arrayBuffer])` —
 * the same crash, now at fetch time. `XMLHttpRequest` with
 * `responseType = 'blob'` reads the file straight into a native `Blob` (backed
 * by RN's `BlobModule`) without ever constructing one from bytes, so it is safe
 * on both platforms.
 */
export async function assetToUploadData(asset: Asset): Promise<Blob> {
  if (asset.uri !== undefined) {
    return uriToBlob(asset.uri);
  }
  return asset.blob;
}

/**
 * Reads a local file `uri` into a `Blob` via `XMLHttpRequest`, used instead of
 * `fetch(uri).blob()` because that crashes on React Native for freshly-written
 * local files (see {@link assetToUploadData}). XHR's blob response is backed by
 * a native blob id and is never constructed from an `ArrayBuffer`, so it is the
 * only reliable way to obtain an upload-ready native `Blob` from a `uri`.
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async -- wraps XHR's callback API in a Promise constructor, which cannot be an async function.
function uriToBlob(uri: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.onload = () => resolve(xhr.response as Blob);
    xhr.onerror = () => reject(new Error(`Failed to read file for upload: ${uri}`));
    xhr.open('GET', uri, true);
    xhr.send(null);
  });
}

/**
 * Returns a URL usable as an `<img src>` preview. Native assets expose their
 * `uri` as-is; web assets get a transient `blob:` object URL the caller is
 * responsible for revoking once the image loads.
 */
export function assetPreviewUrl(asset: Asset): string {
  if (asset.uri !== undefined) return asset.uri;
  return URL.createObjectURL(asset.blob);
}
